import type { PlayerNetState } from '@mn/shared';
import { NetClient } from './client.js';
import { connectWallet, eagerConnectWallet, currentWallet, walletFromUrl, refFromUrl, clearStoredRef, disconnectWallet } from './wallet.js';
import { signInWallet, clearSessionToken, getSessionToken } from './auth.js';
import { checkAccess, type AccessResult } from './access.js';
import {
  getPlayerProfile,
  createPlayerProfile,
  updatePlayerProfile,
  NOT_FOUND,
  type PlayerView,
  type ProfileInput,
} from './api.js';

export { NetClient } from './client.js';
export type { NetEvent } from './client.js';
export { RemotePlayers } from './remote-players.js';
export { ChatOverlay } from './chat-overlay.js';
export { InteractionMenu } from './interaction-menu.js';
export { realmForArea, DEFAULT_REALM, REALM_LABELS, isPvpRealm } from './realm.js';
export { Tombstones } from './tombstones.js';
export { TradePanel } from './trade-panel.js';
export { connectWallet, disconnectWallet, hasWallet, currentWallet, eagerConnectWallet, walletFromUrl } from './wallet.js';
export { getSessionToken, clearSessionToken, signInWallet } from './auth.js';
export { checkAccess } from './access.js';
export type { AccessResult, AccessMode } from './access.js';
export { NOT_FOUND } from './api.js';
export type { PlayerView, ProfileInput } from './api.js';

/** Account-lookup outcome the entry router branches on. */
export type AccountLookup =
  | { kind: 'new'; wallet: string }
  | { kind: 'existing'; wallet: string; view: PlayerView }
  | { kind: 'offline'; wallet: string };

// ── Engine ↔ protocol direction mapping. ──
// The Phaser engine uses uppercase DIRECTION ('UP'); the protocol uses lowercase ('up').
const DIR_DOWN: PlayerNetState['dir'] = 'down';
export function toNetDir(engineDir: string | undefined): PlayerNetState['dir'] {
  switch (engineDir) {
    case 'UP':
      return 'up';
    case 'LEFT':
      return 'left';
    case 'RIGHT':
      return 'right';
    case 'DOWN':
      return 'down';
    default:
      return DIR_DOWN;
  }
}

// ── App-level singleton wrapping wallet + access + realtime client. ──
// Scenes use this; the title scene runs the play-gate, the world scene wires movement/chat.
class NetManager {
  #client: NetClient | null = null;
  #access: AccessResult | null = null;
  /** The wallet the entry router locked in for this play session. */
  #sessionWallet: string | null = null;
  /** True when the active account was hydrated from the OFFLINE cache (not a fresh server load); the
   * world uses this to skip the server party autosave so a stale cached team can't overwrite the server. */
  #partyFromCacheOnly = false;
  get partyFromCacheOnly(): boolean {
    return this.#partyFromCacheOnly;
  }

  get client(): NetClient | null {
    return this.#client;
  }

  get access(): AccessResult | null {
    return this.#access;
  }

  /** The wallet we resolved for this session (entry router → URL hand-off → injected provider). */
  get wallet(): string | null {
    return this.#sessionWallet ?? this.#access?.wallet ?? walletFromUrl() ?? currentWallet();
  }

  /**
   * Resolve the mandatory session wallet for the MMO entry flow. Order:
   *   1. `?wallet=` URL hand-off from the landing page (no popup),
   *   2. an already-trusted injected wallet (silent),
   *   3. (only when `interactive`) an explicit Connect Wallet popup.
   * Returns the base58 address or null when no wallet could be resolved.
   */
  async resolveWallet(opts: { interactive: boolean } = { interactive: false }): Promise<string | null> {
    // Identity = the wallet you CONNECT + SIGN. The `?wallet=` URL hand-off is only a hint; ownership
    // MUST be proven by signature — otherwise anyone could load / act as any account just by naming its
    // wallet in the URL (the account-takeover bug). Connect the injected provider first (silent when
    // already trusted; popup only when interactive) so we can actually sign.
    let wallet = currentWallet() ?? (await eagerConnectWallet());
    if (!wallet && opts.interactive) {
      wallet = await connectWallet();
    }
    // Last resort: the URL hand-off address. Without a connected provider we can't sign it, so it
    // won't earn a token and entry stays gated below — which is what blocks impersonation.
    wallet = wallet ?? walletFromUrl();
    if (!wallet) return null;
    this.#sessionWallet = wallet;
    // Prove ownership → session token (a single signature popup; a still-valid token is reused with no
    // popup). BLOCKING: the server now rejects every account request without a token, so we must not
    // treat the player as this wallet until ownership is proven. No token → null → the connect prompt.
    await signInWallet(wallet);
    if (!getSessionToken(wallet)) return null;
    return wallet;
  }

  /**
   * Look up the account behind a wallet to branch new-vs-existing onboarding.
   *   - `existing` → an account row was returned (load its profile into the world),
   *   - `new`      → the api responded 404 (no account yet → run profile setup),
   *   - `offline`  → api unreachable / unconfigured (treat as new so onboarding still works).
   */
  async lookupAccount(wallet: string): Promise<AccountLookup> {
    this.#sessionWallet = wallet;
    // Retry transient/cold-start failures so a RETURNING player is never sent back to onboarding.
    let res = await getPlayerProfile(wallet);
    for (let i = 0; i < 2 && res === null; i++) {
      await new Promise((r) => setTimeout(r, 1200));
      res = await getPlayerProfile(wallet);
    }
    if (res === NOT_FOUND) return { kind: 'new', wallet };
    if (res === null) {
      // Still unreachable: fall back to a locally-cached account so a known wallet keeps playing
      // (NEVER re-onboard). Only a brand-new, never-seen wallet should ever hit profile setup.
      try {
        const cached = localStorage.getItem(`mn_acct_${wallet}`);
        if (cached) {
          // Loaded from the OFFLINE cache (server unreachable). The cached party may be stale, so block
          // the world's server autosave until a fresh authoritative load confirms it — otherwise a
          // stale cached team could be written back over a newer server party.
          this.#partyFromCacheOnly = true;
          return { kind: 'existing', wallet, view: JSON.parse(cached) as PlayerView };
        }
      } catch {
        /* ignore */
      }
      return { kind: 'offline', wallet };
    }
    try {
      localStorage.setItem(`mn_acct_${wallet}`, JSON.stringify(res));
    } catch {
      /* ignore */
    }
    // Fresh, authoritative server load → autosave is safe.
    this.#partyFromCacheOnly = false;
    return { kind: 'existing', wallet, view: res };
  }

  /**
   * Run the hold-to-play gate. Resolution order for the wallet:
   *   1. `?wallet=` URL hand-off from the landing page (no popup),
   *   2. an already-connected / silently-trusted injected wallet,
   *   3. (only when `interactive`) an explicit connect popup.
   * Always resolves to an AccessResult — demo mode on any failure, so play is never blocked.
   */
  async runAccessGate(opts: { interactive: boolean } = { interactive: false }): Promise<AccessResult> {
    let wallet = walletFromUrl() ?? currentWallet() ?? (await eagerConnectWallet());
    if (!wallet && opts.interactive) {
      wallet = await connectWallet();
    }
    this.#access = await checkAccess(wallet);
    return this.#access;
  }

  /** Explicit "Connect Wallet" action. Returns the connected address (or null if rejected). */
  async connect(): Promise<string | null> {
    const wallet = (await connectWallet()) ?? walletFromUrl() ?? currentWallet();
    if (wallet) {
      // refresh the access result against the freshly-connected wallet
      this.#access = await checkAccess(wallet);
      // Background sign-in for the freshly-connected wallet (non-blocking, best-effort).
      void signInWallet(wallet);
    }
    return wallet;
  }

  /** Disconnect the wallet AND forget the session token (logout). Never throws. */
  async disconnect(): Promise<void> {
    clearSessionToken();
    await disconnectWallet();
    this.#sessionWallet = null;
    this.#access = null;
  }

  /** The current Phase-1 session token, or null when tokenless. Pure read. */
  get sessionToken(): string | null {
    return getSessionToken(this.wallet);
  }

  /**
   * Create the account for the current session wallet (new-player profile setup).
   * Returns the created PlayerView, or null offline / walletless (caller falls back to local).
   */
  async createAccount(profile: Omit<ProfileInput, 'wallet'>): Promise<PlayerView | null> {
    const wallet = this.wallet;
    if (!wallet) return null;
    // Thread the referrer (?ref=, persisted) so the server records it + grants the one-time bonus.
    // Self-referral is dropped client-side; the server validates again before paying out.
    const ref = refFromUrl();
    const view = await createPlayerProfile({ ...profile, wallet, ref: ref && ref !== wallet ? ref : undefined });
    if (view) {
      clearStoredRef(); // consumed — never re-apply on a later (no-op) create
      try {
        localStorage.setItem(`mn_acct_${wallet}`, JSON.stringify(view)); // skip onboarding next time
      } catch {
        /* ignore */
      }
    }
    return view;
  }

  /**
   * Persist edits to name / PFP / bio on the existing account (in-world profile page).
   * Returns the refreshed PlayerView, or null offline / walletless.
   */
  async saveProfile(patch: Omit<ProfileInput, 'wallet'>): Promise<PlayerView | null> {
    const wallet = this.wallet;
    if (!wallet) return null;
    return updatePlayerProfile({ ...patch, wallet });
  }

  /** Lazily create the realtime client bound to the resolved session wallet. */
  ensureClient(): NetClient {
    if (!this.#client) {
      this.#client = new NetClient(this.wallet);
    }
    return this.#client;
  }
}

export const netManager = new NetManager();
